import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import koLocale from "@fullcalendar/core/locales/ko";
import { toast } from "react-toastify";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import { axiosInstance } from "../../../../api/axios";
import { Icon } from "../../../icons";

const quickIcon = (name) => <Icon name={name} width={22} height={22} />;

const QUICK_ACTIONS = [
  {
    title: "평가 시작",
    path: "/assessment/wizard",
    color: "#2563eb",
    icon: quickIcon("todoIcon"),
  },
  {
    title: "보고서 다운로드",
    path: "/monitoring/integrated-report",
    color: "#4f46e5",
    icon: quickIcon("chartIcon"),
  },
  {
    title: "공정 관리",
    path: "/standard/process",
    color: "#0f766e",
    icon: quickIcon("ganttIcon"),
  },
  {
    title: "설비 관리",
    path: "/standard/machine",
    color: "#0284c7",
    icon: quickIcon("repairIcon"),
  },
  {
    title: "유해·위험요인 관리",
    path: "/register/cause",
    color: "#ea580c",
    icon: quickIcon("fireIcon"),
  },
  {
    title: "물질안전보건자료(MSDS) 관리",
    path: "/standard/msds",
    color: "#7c3aed",
    icon: quickIcon("folderIcon"),
  },
  {
    title: "TBM 관리",
    path: "/monitoring/tbm",
    color: "#0891b2",
    icon: quickIcon("officeIcon"),
  },
  {
    title: "안전점검",
    path: "/monitoring/inspection",
    color: "#dc2626",
    icon: quickIcon("viewIcon"),
  },
  {
    title: "교육 일정",
    path: "/monitoring/education",
    color: "#4f46e5",
    icon: quickIcon("calendarIcon"),
  },
  {
    title: "제안 검토",
    path: "/worker/hazard-review",
    color: "#06b6d4",
    icon: quickIcon("messageIcon"),
  },
];

const MOBILE_HOME_ACTIONS = [
  {
    title: "근로자 유해·위험요인 제안",
    path: "/worker/hazard-proposal",
    color: "#06b6d4",
    icon: quickIcon("qrIcon"),
  },
  {
    title: "TBM 관리",
    path: "/monitoring/tbm",
    color: "#0891b2",
    icon: quickIcon("officeIcon"),
  },
  {
    title: "교육 일정",
    path: "/monitoring/education",
    color: "#4f46e5",
    icon: quickIcon("calendarIcon"),
  },
  {
    title: "안전점검",
    path: "/monitoring/inspection",
    color: "#dc2626",
    icon: quickIcon("viewIcon"),
  },
];

const emptyDashboard = {
  pendingHazards: 0,
  approvalPending: 0,
  approvalCompleted: 0,
  improveWaiting: 0,
  improveOverdue: 0,
  improveCompleted: 0,
  pendingProposals: 0,
  inspectionActionOpen: 0,
  inspectionActionOverdue: 0,
  todayTbmCompleted: 0,
  todayTbmMissed: 0,
  todayTbmUnregistered: 0,
  msdsExpiring: 0,
  msdsExpired: 0,
  monthlyCompletion: [],
  upcomingEducation: [],
  upcomingTbm: [],
  msdsItems: [],
  tbmCalendar: [],
  safetyCalendar: [],
  calendarEvents: [],
  isLoading: true,
  isError: false,
};

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const uniqueRowsBy = (rows, getKey) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const key = getKey(row);
    const normalizedKey =
      key === undefined || key === null || key === ""
        ? `row-${index}`
        : String(key);
    if (!map.has(normalizedKey)) {
      map.set(normalizedKey, row);
    }
  });
  return Array.from(map.values());
};

const isCompletedImprove = (row) => toNumber(row?.improve_status) >= 20;
const isOverdueImprove = (row) =>
  row?.expected_date &&
  !isCompletedImprove(row) &&
  new Date(row.expected_date).setHours(0, 0, 0, 0) <
    new Date().setHours(0, 0, 0, 0);

const formatDateParam = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getDateKey = (value) => String(value || "").slice(0, 10);
const daysBetween = (from, to) => {
  const fromTime = new Date(`${getDateKey(from)}T00:00:00`).getTime();
  const toTime = new Date(`${getDateKey(to)}T00:00:00`).getTime();
  if (!Number.isFinite(fromTime) || !Number.isFinite(toTime)) return null;
  return Math.ceil((toTime - fromTime) / 86400000);
};

const getCalendarLabel = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  return {
    day: String(date.getDate()),
    weekday: weekdays[date.getDay()] || "",
  };
};

const buildSafetyCalendarDays = ({
  start,
  days = 7,
  tbmItems = [],
  educationItems = [],
  inspectionItems = [],
  msdsItems = [],
}) =>
  Array.from({ length: days }, (_, index) => {
    const date = formatDateParam(addDays(start, index));
    const tbmMissedCount = tbmItems
      .filter((item) => getDateKey(item.date || item.session_day) === date)
      .reduce((sum, item) => sum + Number(item.missed || 0), 0);
    const educationCount = educationItems.filter(
      (item) => getDateKey(item.edu_day) === date
    ).length;
    const inspectionCount = inspectionItems.filter(
      (item) => getDateKey(item.due_day || item.due_at) === date
    ).length;
    const msdsDueCount = msdsItems.filter(
      (item) => getDateKey(item.expire_at) === date
    ).length;
    const items = [];

    if (msdsDueCount > 0)
      items.push({
        label: `MSDS 마감 ${msdsDueCount}`,
        shortLabel: `MSDS ${msdsDueCount}`,
        color: "error",
      });
    if (inspectionCount > 0)
      items.push({ label: `조치 ${inspectionCount}`, color: "warning" });
    if (tbmMissedCount > 0)
      items.push({ label: `TBM ${tbmMissedCount}`, color: "error" });
    if (educationCount > 0)
      items.push({ label: `교육 ${educationCount}`, color: "primary" });

    return { date, ...getCalendarLabel(date), items };
  });

const calendarSourceMeta = {
  education: { label: "교육", color: "#2563eb", bg: "#eff6ff" },
  tbm: { label: "TBM", color: "#0891b2", bg: "#ecfeff" },
  inspection: { label: "점검 조치", color: "#f97316", bg: "#fff7ed" },
  msds: { label: "MSDS", color: "#7c3aed", bg: "#f5f3ff" },
};

const getCalendarEventMeta = (event = {}) => {
  if (event.source === "tbm") {
    if (event.status === "completed")
      return { label: "TBM", color: "#16a34a", bg: "#f0fdf4" };
    if (event.status === "missed")
      return { label: "TBM", color: "#dc2626", bg: "#fef2f2" };
    return { label: "TBM", color: "#f59e0b", bg: "#fffbeb" };
  }
  if (event.source === "inspection") {
    if (event.status === "overdue")
      return { label: "조치", color: "#dc2626", bg: "#fef2f2" };
    if (event.status === "done")
      return { label: "조치", color: "#16a34a", bg: "#f0fdf4" };
    return { label: "조치", color: "#f97316", bg: "#fff7ed" };
  }
  return (
    calendarSourceMeta[event.source] || {
      label: "일정",
      color: "#475569",
      bg: "#f8fafc",
    }
  );
};

const getSafetySummaryChipSx = (item = {}) => {
  const label = String(item.label || item.shortLabel || "").toLowerCase();
  const meta =
    item.meta ||
    (label.includes("msds")
      ? calendarSourceMeta.msds
      : label.includes("tbm")
      ? { label: "TBM", color: "#dc2626", bg: "#fef2f2" }
      : label.includes("교육")
      ? calendarSourceMeta.education
      : label.includes("조치")
      ? calendarSourceMeta.inspection
      : null);

  if (meta) {
    return {
      height: 22,
      borderRadius: "7px",
      border: 0,
      bgcolor: meta.bg,
      color: meta.color,
      fontWeight: 900,
      "& .MuiChip-label": { px: 0.85 },
    };
  }

  return {
    height: 22,
    borderRadius: "7px",
    border: "1px solid #e2e8f0",
    bgcolor: "#f8fafc",
    color: "#475569",
    fontWeight: 900,
    "& .MuiChip-label": { px: 0.85 },
  };
};

const normalizeCalendarEvents = (events = []) =>
  events
    .filter((event) => {
      const source = String(event.source || event.type || "").toLowerCase();
      if (source !== "tbm") return true;
      return event.status === "missed";
    })
    .map((event) => {
      const meta = getCalendarEventMeta(event);
      const isMissedTbm =
        String(event.source || event.type || "").toLowerCase() === "tbm" &&
        event.status === "missed";
      const isMsdsDue =
        String(event.source || event.type || "").toLowerCase() === "msds";
      return {
        id: event.id,
        title: isMissedTbm
          ? `TBM 미실시 - ${event.title || "대상 팀"}`
          : isMsdsDue
          ? `MSDS 마감 - ${event.title || "제품"}`
          : event.title || meta.label,
        start: event.start,
        end: event.end,
        allDay: Boolean(event.allDay),
        backgroundColor: meta.bg,
        borderColor: meta.color,
        textColor: "#0f172a",
        extendedProps: {
          ...event,
          meta,
        },
      };
    });

const buildLegacyCalendarEvents = ({
  tbmItems = [],
  educationItems = [],
  inspectionItems = [],
  msdsItems = [],
}) => [
  ...tbmItems.flatMap((day) =>
    (day.sessions || [])
      .filter((session) => session.status === "missed")
      .map((session) => ({
        id: `tbm-${session.id || `${day.date}-${session.team_name || "team"}`}`,
        source: "tbm",
        type: "tbm",
        title: session.team_name || "TBM",
        start: session.session_date_input || session.session_day || day.date,
        status: session.status,
        allDay: Boolean(session.virtual),
        raw: session,
      }))
  ),
  ...educationItems.map((item) => ({
    id: `education-${item.id}`,
    source: "education",
    type: "education",
    title: item.title || "교육 일정",
    start: item.calendar_start || item.edu_date_input || item.edu_day,
    end: item.calendar_end,
    status: item.status,
    allDay: false,
    raw: item,
  })),
  ...inspectionItems.map((item) => ({
    id: `inspection-action-${item.id}`,
    source: "inspection",
    type: "inspection_action",
    title: item.title || item.action_title || "점검 조치",
    start: item.due_at_input || item.due_day,
    status: item.effective_sla_status || item.sla_status,
    allDay: false,
    raw: item,
  })),
  ...msdsItems
    .filter((item) => item.expire_at)
    .map((item) => ({
      id: `msds-${item.id}`,
      source: "msds",
      type: "msds_expire",
      title: item.product_name || "MSDS",
      start: item.expire_at,
      status: "expiring",
      allDay: true,
      raw: item,
    })),
];

const getTodayTbmSummary = (tbmCalendar = [], todayKey) => {
  const today = (Array.isArray(tbmCalendar) ? tbmCalendar : []).find(
    (day) => day.date === todayKey
  );
  if (!today) {
    return {
      missed: 0,
      unregistered: 0,
      completed: 0,
      total: 0,
    };
  }

  return {
    missed: toNumber(today.missed),
    unregistered: toNumber(today.unregistered),
    completed: toNumber(today.completed),
    total: toNumber(today.total),
  };
};

const getRecentMonths = () => {
  const current = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(
      current.getFullYear(),
      current.getMonth() - (5 - index),
      1
    );
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      label: `${date.getMonth() + 1}월`,
    };
  });
};

const panelSx = {
  border: "1px solid",
  borderColor: "#e5e7eb",
  borderRadius: "8px",
  bgcolor: "#fff",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.035)",
};

const tallPanelSx = {
  minHeight: 0,
};

const lowerPanelSx = {
  minHeight: 0,
};

function SectionPanel({ title, children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: { xs: 2, md: 2 },
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <Typography sx={{ fontWeight: 900, color: "#111827", mb: 1.5 }}>
        {title}
      </Typography>
      <Box
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {children}
      </Box>
    </Paper>
  );
}

function KpiBoard({ items }) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 0,
        width: "100%",
        flexShrink: 0,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        {items.map((item, index) => (
          <Box
            key={item.label}
            component="div"
            title={item.definition || undefined}
            aria-label={item.definition || undefined}
            sx={{
              border: 0,
              font: "inherit",
              minHeight: { xs: 112, md: 124, lg: 118 },
              p: { xs: 2, md: 2 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              borderRight: {
                lg: index < items.length - 1 ? "1px solid #eef2f7" : "none",
              },
              borderBottom: {
                xs: index < 2 ? "1px solid #eef2f7" : "none",
                lg: "none",
              },
              bgcolor: "#fff",
              cursor: "default",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1.25}
              sx={{ minWidth: 0 }}
            >
              <Avatar
                sx={{
                  width: 38,
                  height: 38,
                  color: item.color,
                  bgcolor: "#f8fafc",
                  border: "1px solid",
                  borderColor: "#e5e7eb",
                }}
              >
                {item.icon}
              </Avatar>
              <Typography
                sx={{
                  fontWeight: 900,
                  color: "#334155",
                  lineHeight: 1.2,
                  textAlign: "center",
                }}
                noWrap
              >
                {item.label}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="baseline"
              justifyContent="center"
              spacing={0.5}
            >
              <Typography
                sx={{
                  fontSize: { xs: 32, md: 36 },
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: 0,
                  color: "#0f172a",
                }}
              >
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 800, color: "#6b7280" }}>
                건
              </Typography>
            </Stack>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function QuickActionCard({ item, onClick }) {
  return (
    <Paper
      component="button"
      type="button"
      elevation={0}
      onClick={onClick}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: { xs: 104, md: 86, xl: 94 },
        p: { xs: 1.75, md: 1.45 },
        border: "1px solid",
        borderColor: "#e5e7eb",
        borderRadius: "8px",
        bgcolor: "#fff",
        cursor: "pointer",
        textAlign: "center",
        transition:
          "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          borderColor: "#cbd5e1",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.055)",
          transform: "translateY(-1px)",
        },
        "&:focus-visible": {
          outline: "2px solid #2563eb",
          outlineOffset: 2,
        },
      }}
    >
      <Stack
        spacing={0.9}
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%" }}
      >
        <Avatar
          sx={{
            width: { xs: 42, md: 36, xl: 40 },
            height: { xs: 42, md: 36, xl: 40 },
            color: item.color,
            bgcolor: "#f8fafc",
            border: "1px solid",
            borderColor: "#e5e7eb",
          }}
        >
          {item.icon}
        </Avatar>
        <Typography
          sx={{
            fontWeight: 900,
            color: "#111827",
            lineHeight: 1.2,
            textAlign: "center",
          }}
        >
          {item.title}
        </Typography>
      </Stack>
    </Paper>
  );
}

const formatMobileScheduleDate = (value) => {
  const dateKey = getDateKey(value);
  if (!dateKey) return "-";

  const date = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateKey;

  return `${date.getMonth() + 1}/${date.getDate()}`;
};

function MobileScheduleItem({
  title,
  subtitle,
  date,
  type,
  color = "primary",
  onClick,
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        width: "100%",
        p: 1.25,
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        bgcolor: "#fff",
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        transition:
          "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:active": {
          transform: "scale(0.99)",
        },
        "&:focus-visible": {
          outline: "2px solid #2563eb",
          outlineOffset: 2,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.1}
        sx={{ minWidth: 0 }}
      >
        <Box
          sx={{
            flex: "0 0 auto",
            width: 46,
            height: 46,
            borderRadius: "8px",
            bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 950,
              color: "#0f172a",
              lineHeight: 1,
            }}
          >
            {date}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ minWidth: 0, mb: 0.35 }}
          >
            <Typography
              variant="body2"
              sx={{
                minWidth: 0,
                flex: 1,
                color: "#111827",
                fontWeight: 950,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            <Chip
              size="small"
              color={color}
              label={type}
              sx={{
                flex: "0 0 auto",
                height: 22,
                borderRadius: "7px",
                fontWeight: 900,
                "& .MuiChip-label": { px: 0.8 },
              }}
            />
          </Stack>
          <Typography
            variant="caption"
            sx={{
              color: "#64748b",
              fontWeight: 800,
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function MobileTodayTbm({ completed, missed, onNavigate }) {
  const missedCount = toNumber(missed);
  const completedCount = toNumber(completed);

  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 1.5,
        width: "100%",
      }}
    >
      <Stack spacing={1.15}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: "#f8fafc",
              color: "#475569",
              border: "1px solid #e5e7eb",
            }}
          >
            {quickIcon("checklistIcon")}
          </Avatar>
          <Typography sx={{ color: "#111827", fontWeight: 950 }}>
            오늘의 TBM
          </Typography>
        </Stack>

        <Stack
          component="button"
          type="button"
          onClick={() => onNavigate("/monitoring/tbm")}
          direction="row"
          spacing={0.9}
          sx={{
            width: "100%",
            p: 0,
            border: 0,
            bgcolor: "transparent",
            font: "inherit",
            cursor: "pointer",
            "&:focus-visible": {
              outline: "2px solid #2563eb",
              outlineOffset: 3,
              borderRadius: "10px",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: 38,
              borderRadius: "9px",
              border: "1px solid #bbf7d0",
              bgcolor: "#f0fdf4",
              color: "#15803d",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 950,
              fontSize: 13,
            }}
          >
            완료 {completedCount}팀
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              height: 38,
              borderRadius: "9px",
              border:
                missedCount > 0 ? "1px solid #fecaca" : "1px solid #e5e7eb",
              bgcolor: missedCount > 0 ? "#fff1f2" : "#f8fafc",
              color: missedCount > 0 ? "#be123c" : "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 950,
              fontSize: 13,
            }}
          >
            미실시 {missedCount}팀
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

function MobileUpcomingSchedule({ educationItems, isLoading, onNavigate }) {
  const mobileScheduleItems = educationItems
    .slice(0, 3)
    .map((item) => ({
      key: `edu-${item.id}`,
      title: item.title || "안전보건교육",
      subtitle:
        `${item.edu_time || ""}${
          item.target_desc ? ` · ${item.target_desc}` : ""
        }`.trim() || "교육 일정",
      date: formatMobileScheduleDate(
        item.edu_day || item.edu_date_input || item.edu_date
      ),
      sortKey: getDateKey(item.edu_day || item.edu_date_input || item.edu_date),
      type: item.edu_type || "교육",
      color: "primary",
      path: "/monitoring/education",
    }))
    .sort((a, b) => String(a.sortKey).localeCompare(String(b.sortKey)));

  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        p: 1.5,
        width: "100%",
      }}
    >
      <Stack spacing={1.15}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: "#f8fafc",
              color: "#475569",
              border: "1px solid #e5e7eb",
            }}
          >
            <CalendarMonthIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ color: "#111827", fontWeight: 950 }}>
            다가오는 일정
          </Typography>
        </Stack>

        <Stack spacing={0.85}>
          {mobileScheduleItems.map((item) => (
            <MobileScheduleItem
              key={item.key}
              title={item.title}
              subtitle={item.subtitle}
              date={item.date}
              type={item.type}
              color={item.color}
              onClick={() => onNavigate(item.path)}
            />
          ))}

          {!mobileScheduleItems.length ? (
            <Box
              sx={{
                px: 1.25,
                py: 1.35,
                border: "1px dashed #dbe3ef",
                borderRadius: "8px",
                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#64748b", fontWeight: 850 }}
              >
                {isLoading
                  ? "교육 일정을 불러오는 중입니다"
                  : "다가오는 안전교육이 없습니다"}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function MobileHomeDashboard({
  kpiItems,
  actions,
  educationItems,
  todayTbm,
  isLoading,
  onNavigate,
}) {
  return (
    <Box
      sx={{
        minHeight: "100%",
        p: 1.5,
        bgcolor: "#f3f6fa",
        overflow: "auto",
      }}
    >
      <Stack spacing={1.5}>
        <KpiBoard
          items={kpiItems.map((item) =>
            isLoading ? { ...item, value: "-" } : item
          )}
        />

        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: 1.5,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1.25,
            }}
          >
            {actions.map((item) => (
              <QuickActionCard
                key={item.title}
                item={item}
                onClick={() => onNavigate(item.path)}
              />
            ))}
          </Box>
        </Paper>

        <MobileTodayTbm
          completed={todayTbm.completed}
          missed={todayTbm.missed}
          onNavigate={onNavigate}
        />

        <MobileUpcomingSchedule
          educationItems={educationItems}
          isLoading={isLoading}
          onNavigate={onNavigate}
        />
      </Stack>
    </Box>
  );
}

function MonthlyChart({ data }) {
  const maxValue = Math.max(1, ...data.map((item) => item.value));

  return (
    <SectionPanel title="월별 개선 완료 현황" sx={lowerPanelSx}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="end"
        sx={{ flex: 1, minHeight: { xs: 190, lg: 145, xl: 170 } }}
      >
        {data.map((item, index) => {
          const height = Math.max(
            30,
            Math.round((item.value / maxValue) * 120)
          );
          const isCurrent = index === data.length - 1;
          return (
            <Stack
              key={`${item.year}-${item.month}`}
              spacing={1}
              alignItems="center"
              sx={{ flex: 1, minWidth: 0 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {item.value}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 34,
                  height,
                  borderRadius: "7px 7px 3px 3px",
                  bgcolor: isCurrent ? "#334155" : "#e5e7eb",
                }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </SectionPanel>
  );
}

const scheduleStatusMeta = {
  pending: { label: "예정", color: "primary" },
  completed: { label: "완료", color: "success" },
  cancelled: { label: "취소", color: "default" },
  unregistered: { label: "미등록", color: "warning" },
  missed: { label: "미실시", color: "error" },
};

function ScheduleList({
  icon,
  title,
  items,
  emptyText,
  renderPrimary,
  renderSecondary,
  renderChip,
}) {
  return (
    <Stack spacing={1.1} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: "#f8fafc",
            color: "#475569",
            border: "1px solid #e5e7eb",
          }}
        >
          {icon}
        </Avatar>
        <Typography variant="body2" sx={{ fontWeight: 900, color: "#111827" }}>
          {title}
        </Typography>
      </Stack>

      <Stack spacing={0.75}>
        {items.map((item) => (
          <Box
            key={`${title}-${item.id}`}
            sx={{
              p: { xs: 1.15, lg: 0.95 },
              border: "1px solid #eef2f7",
              borderRadius: "8px",
              bgcolor: "#fff",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.025)",
              transition:
                "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease, background-color 180ms ease",
              "&:hover": {
                borderColor: "#c7d2fe",
                bgcolor: "#fbfdff",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.095)",
                transform: "translateY(-2px)",
              },
              "&:focus-within": {
                borderColor: "#a5b4fc",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.12)",
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 900, color: "#0f172a" }}
                >
                  {renderPrimary(item)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    display: "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    lineHeight: 1.35,
                  }}
                >
                  {renderSecondary(item)}
                </Typography>
              </Box>
              <Box sx={{ flex: "0 0 auto", ml: "auto", alignSelf: "center" }}>
                {renderChip(item)}
              </Box>
            </Stack>
          </Box>
        ))}

        {!items.length ? (
          <Box
            sx={{
              px: 1.25,
              py: { xs: 1.15, lg: 0.9 },
              border: "1px dashed #dbe3ef",
              borderRadius: "8px",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontWeight: 800 }}
            >
              {emptyText}
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
}

function SafetyCalendarStrip({ days = [], onOpen, onDayClick }) {
  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0 }}
        >
          <Avatar
            sx={{
              width: 30,
              height: 30,
              bgcolor: "#f8fafc",
              color: "#475569",
              border: "1px solid #e5e7eb",
            }}
          >
            <CalendarMonthIcon fontSize="small" />
          </Avatar>
          <Typography
            variant="body2"
            sx={{ fontWeight: 900, color: "#111827" }}
          >
            안전상황 캘린더
          </Typography>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          onClick={onOpen}
          startIcon={<CalendarMonthIcon fontSize="small" />}
          sx={{
            minWidth: 0,
            height: 30,
            px: 1.1,
            borderRadius: "8px",
            borderColor: "#dbe3ef",
            color: "#334155",
            bgcolor: "#fff",
            fontWeight: 900,
            whiteSpace: "nowrap",
            "&:hover": {
              borderColor: "#cbd5e1",
              bgcolor: "#f8fafc",
            },
          }}
        >
          전체 보기
        </Button>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: 0.75,
        }}
      >
        {days.map((day) => (
          <Box
            key={day.date}
            component="button"
            type="button"
            onClick={() => onDayClick?.(day)}
            sx={{
              width: "100%",
              appearance: "none",
              textAlign: "left",
              cursor: "pointer",
              minHeight: { xs: 88, lg: 82 },
              p: { xs: 1, lg: 1.05 },
              border: "1px solid #eef2f7",
              borderRadius: "8px",
              bgcolor: "#fbfdff",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.025)",
              overflow: "hidden",
              transition:
                "border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease, background-color 180ms ease",
              "&:hover": {
                borderColor: "#c7d2fe",
                bgcolor: "#fff",
                boxShadow: "0 12px 28px rgba(79, 70, 229, 0.095)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Stack spacing={0.85} alignItems="stretch" sx={{ height: "100%" }}>
              <Stack
                direction="row"
                alignItems="baseline"
                spacing={0.55}
                sx={{
                  justifyContent: "flex-start",
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontWeight: 950,
                    fontSize: 17,
                    lineHeight: 1,
                  }}
                >
                  {day.day}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", fontWeight: 900, lineHeight: 1 }}
                >
                  {day.weekday}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={0.45}
                useFlexGap
                flexWrap="wrap"
                alignItems="flex-start"
                justifyContent="flex-start"
                sx={{ width: "100%", minHeight: 28 }}
              >
                {day.items.slice(0, 3).map((item, index) => (
                  <Box
                    key={`${day.date}-${item.label}-${index}`}
                    title={item.label}
                    sx={{
                      height: 22,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 0.75,
                      borderRadius: "7px",
                      border: "1px solid",
                      borderColor:
                        item.color === "error"
                          ? "#fecaca"
                          : item.color === "warning"
                          ? "#fed7aa"
                          : item.color === "primary"
                          ? "#60a5fa"
                          : "#bbf7d0",
                      bgcolor:
                        item.color === "error"
                          ? "#fef2f2"
                          : item.color === "warning"
                          ? "#fff7ed"
                          : item.color === "primary"
                          ? "#dbeafe"
                          : "#f0fdf4",
                      color:
                        item.color === "error"
                          ? "#dc2626"
                          : item.color === "warning"
                          ? "#d97706"
                          : item.color === "primary"
                          ? "#2563eb"
                          : "#16a34a",
                      maxWidth: "100%",
                      boxShadow:
                        item.color === "primary"
                          ? "inset 0 0 0 1px rgba(37, 99, 235, 0.28), 0 1px 2px rgba(37, 99, 235, 0.12)"
                          : "none",
                      fontSize: 10.5,
                      fontWeight: 900,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.shortLabel || item.label}
                  </Box>
                ))}
                {day.items.length > 3 ? (
                  <Box
                    sx={{
                      height: 22,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 0.75,
                      borderRadius: "7px",
                      border: "1px solid #e2e8f0",
                      maxWidth: "100%",
                      bgcolor: "#fff",
                      color: "#475569",
                      fontSize: 10.5,
                      fontWeight: 900,
                    }}
                  >
                    +{day.items.length - 3}
                  </Box>
                ) : null}
                {!day.items.length ? (
                  <Box
                    sx={{
                      width: 22,
                      height: 4,
                      borderRadius: 999,
                      bgcolor: "#cbd5e1",
                      mt: 0.45,
                    }}
                  />
                ) : null}
              </Stack>
            </Stack>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

function SafetyCalendarDayDialog({ open, onClose, day, events = [] }) {
  const dayEvents = useMemo(() => {
    if (!day?.date) return [];
    return events.filter((event) => getDateKey(event.start) === day.date);
  }, [day?.date, events]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          pb: 1.5,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.1}
            sx={{ minWidth: 0 }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#f8fafc",
                color: "#334155",
                border: "1px solid #e5e7eb",
              }}
            >
              <CalendarMonthIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 950, color: "#0f172a", lineHeight: 1.2 }}
              >
                {day?.date || "일정 상세"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#64748b", fontWeight: 800 }}
              >
                안전상황 상세
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              color: "#475569",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc" }}>
        {day?.items?.length ? (
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            sx={{
              minHeight: 28,
              margin: 1.25,
            }}
          >
            {day.items.map((item, index) => (
              <Chip
                key={`${item.label}-${index}`}
                size="small"
                label={item.label}
                sx={getSafetySummaryChipSx(item)}
              />
            ))}
          </Stack>
        ) : null}

        <Stack spacing={1}>
          {dayEvents.length ? (
            dayEvents.map((event) => {
              const meta = event.extendedProps?.meta || {};
              return (
                <Paper
                  key={event.id || `${event.title}-${event.start}`}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    bgcolor: "#fff",
                  }}
                >
                  <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: meta.color || "#64748b",
                          flex: "0 0 auto",
                          boxShadow: "0 0 0 3px rgba(15, 23, 42, 0.04)",
                        }}
                      />
                      <Chip
                        size="small"
                        label={meta.label || "일정"}
                        sx={{
                          height: 22,
                          borderRadius: "7px",
                          bgcolor: meta.bg || "#f8fafc",
                          color: meta.color || "#334155",
                          fontWeight: 900,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#64748b", fontWeight: 800 }}
                      >
                        {getDateKey(event.start)}
                      </Typography>
                    </Stack>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        color: "#0f172a",
                        lineHeight: 1.45,
                      }}
                    >
                      {event.title}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px dashed #cbd5e1",
                borderRadius: "8px",
                bgcolor: "#fff",
              }}
            >
              <Typography sx={{ color: "#64748b", fontWeight: 900 }}>
                상세 항목이 없습니다.
              </Typography>
            </Paper>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

const buildSafetyCalendarDetailDay = (dateKey, events = []) => {
  const dayEvents = events.filter(
    (event) => getDateKey(event.start) === dateKey
  );
  const itemMap = new Map();

  dayEvents.forEach((event) => {
    const source = String(
      event.extendedProps?.source ||
        event.extendedProps?.type ||
        event.source ||
        ""
    ).toLowerCase();
    const meta =
      event.extendedProps?.meta ||
      getCalendarEventMeta(event.extendedProps || event);
    const itemKey = meta.label || source || "일정";
    const color = source.includes("education")
      ? "primary"
      : source.includes("inspection")
      ? "warning"
      : source.includes("tbm") || source.includes("msds")
      ? "error"
      : "default";
    const prev = itemMap.get(itemKey) || {
      label: itemKey,
      shortLabel: itemKey,
      color,
      meta,
      count: 0,
    };
    itemMap.set(itemKey, {
      ...prev,
      count: prev.count + 1,
      label: `${itemKey} ${prev.count + 1}`,
      shortLabel: `${itemKey} ${prev.count + 1}`,
      meta,
    });
  });

  return {
    date: dateKey,
    ...getCalendarLabel(dateKey),
    items: Array.from(itemMap.values()),
  };
};

function SafetyCalendarEventContent({ event }) {
  const meta = event.extendedProps?.meta || {};

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.55,
        minWidth: 0,
        px: 0.55,
        py: 0.25,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: meta.color || "#64748b",
          flex: "0 0 auto",
        }}
      />
      <Typography
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 11,
          fontWeight: 900,
          color: "#0f172a",
          lineHeight: 1.35,
        }}
      >
        {event.title}
      </Typography>
    </Box>
  );
}

function SafetyCalendarDialog({
  open,
  onClose,
  events,
  isMobile,
  onDateClick,
}) {
  const calendarRef = useRef(null);
  const [calendarTitle, setCalendarTitle] = useState("");
  const legendItems = [
    calendarSourceMeta.tbm,
    calendarSourceMeta.education,
    calendarSourceMeta.inspection,
    calendarSourceMeta.msds,
  ];
  const moveCalendar = (action) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    calendarApi[action]();
    setCalendarTitle(calendarApi.view.title);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: { xs: "100%", md: "88vh" },
          borderRadius: { xs: 0, md: "10px" },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, md: 2.4 },
          py: 1.6,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.1}
            sx={{ minWidth: 0 }}
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#f8fafc",
                color: "#334155",
                border: "1px solid #e5e7eb",
              }}
            >
              <CalendarMonthIcon fontSize="small" />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 950, color: "#0f172a", lineHeight: 1.2 }}
              >
                안전상황 캘린더
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              color: "#475569",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: { xs: 1.5, md: 2 },
          bgcolor: "#f8fafc",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          "& .fc": {
            width: "100%",
            height: "100%",
            fontFamily: "inherit",
          },
          "& .fc-scrollgrid": {
            border: "1px solid #e5e7eb !important",
            borderRadius: "10px",
            overflow: "hidden",
            background: "#fff",
          },
          "& .fc-col-header-cell": {
            background: "#f8fafc",
            borderColor: "#e5e7eb !important",
          },
          "& .fc-col-header-cell-cushion": {
            padding: "8px 0",
            color: "#475569",
            fontWeight: 950,
            textDecoration: "none",
          },
          "& .fc-daygrid-day": {
            borderColor: "#eef2f7 !important",
          },
          "& .fc-daygrid-day-number": {
            padding: "7px 8px !important",
            color: "#0f172a",
            fontWeight: 900,
            textDecoration: "none",
          },
          "& .fc-day-today": {
            background: "#f8fbff !important",
          },
          "& .fc-daygrid-event": {
            borderRadius: "7px",
            borderWidth: "1.5px !important",
            borderStyle: "solid !important",
            boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
            backgroundClip: "padding-box",
          },
          "& .fc-daygrid-more-link": {
            color: "#475569",
            fontWeight: 900,
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={1.2}
          sx={{ mb: 1.25 }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={() => moveCalendar("prev")}
              sx={{
                minWidth: 34,
                height: 32,
                px: 0,
                borderRadius: "8px",
                borderColor: "#dbe3ef",
                color: "#334155",
                bgcolor: "#fff",
                fontWeight: 950,
              }}
            >
              {"<"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => moveCalendar("next")}
              sx={{
                minWidth: 34,
                height: 32,
                px: 0,
                borderRadius: "8px",
                borderColor: "#dbe3ef",
                color: "#334155",
                bgcolor: "#fff",
                fontWeight: 950,
              }}
            >
              {">"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => moveCalendar("today")}
              sx={{
                height: 32,
                px: 1.25,
                borderRadius: "8px",
                borderColor: "#dbe3ef",
                color: "#334155",
                bgcolor: "#fff",
                fontWeight: 900,
              }}
            >
              오늘
            </Button>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: "flex-start", md: "center" }}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <Typography
              sx={{
                color: "#111827",
                fontWeight: 950,
                fontSize: { xs: 18, md: 22 },
                lineHeight: 1.15,
                whiteSpace: "nowrap",
              }}
            >
              {calendarTitle}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            spacing={0.65}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            sx={{
              minWidth: { xs: 0, md: 250 },
              maxWidth: { xs: "100%", md: 420 },
            }}
          >
            {legendItems.map((item) => (
              <Chip
                key={item.label}
                size="small"
                label={item.label}
                sx={{
                  height: 24,
                  bgcolor: item.bg,
                  color: item.color,
                  border: "1px solid",
                  borderColor: item.color,
                  fontWeight: 900,
                  "& .MuiChip-label": { px: 0.85 },
                }}
              />
            ))}
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            height: { xs: "calc(100vh - 150px)", md: "calc(88vh - 144px)" },
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            locale={koLocale}
            initialView="dayGridMonth"
            headerToolbar={false}
            buttonText={{ today: "오늘" }}
            events={events}
            height="100%"
            fixedWeekCount={false}
            showNonCurrentDates={false}
            dayMaxEvents={isMobile ? 3 : 5}
            moreLinkText={(count) => `+${count}`}
            eventContent={(eventInfo) => (
              <SafetyCalendarEventContent event={eventInfo.event} />
            )}
            dateClick={(info) =>
              onDateClick?.(buildSafetyCalendarDetailDay(info.dateStr, events))
            }
            eventClick={(info) => {
              const dateKey = getDateKey(
                info.event.startStr || info.event.start
              );
              if (dateKey)
                onDateClick?.(buildSafetyCalendarDetailDay(dateKey, events));
            }}
            datesSet={(info) => setCalendarTitle(info.view.title)}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function UpcomingSchedulePanel({
  educationItems,
  calendarDays,
  isLoading,
  onOpenCalendar,
  onOpenCalendarDay,
}) {
  return (
    <SectionPanel title="다가오는 일정" sx={lowerPanelSx}>
      <Stack
        spacing={{ xs: 1.6, lg: 1.15 }}
        divider={<Divider flexItem />}
        sx={{ flex: 1 }}
      >
        <SafetyCalendarStrip
          days={calendarDays}
          onOpen={onOpenCalendar}
          onDayClick={onOpenCalendarDay}
        />

        <ScheduleList
          icon={<CalendarMonthIcon fontSize="small" />}
          title="안전보건교육"
          items={educationItems.slice(0, 2)}
          emptyText={
            isLoading
              ? "교육 일정을 불러오는 중입니다"
              : "다가오는 교육 일정이 없습니다"
          }
          renderPrimary={(item) => item.title || "-"}
          renderSecondary={(item) =>
            `${item.edu_day || "-"} ${item.edu_time || ""}${
              item.target_desc ? ` · ${item.target_desc}` : ""
            }`
          }
          renderChip={(item) => {
            const meta =
              scheduleStatusMeta[item.status] || scheduleStatusMeta.pending;
            return (
              <Chip
                size="small"
                color={meta.color}
                label={item.edu_type || meta.label}
              />
            );
          }}
        />
      </Stack>
    </SectionPanel>
  );
}

function RecentActivityPanel({ activities, onItemClick }) {
  return (
    <SectionPanel title="후속 처리 알림" sx={tallPanelSx}>
      <Stack
        divider={<Divider flexItem />}
        spacing={0}
        sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
      >
        {activities.map((activity) => (
          <Stack
            key={activity.title}
            component={activity.path ? "button" : "div"}
            type={activity.path ? "button" : undefined}
            onClick={
              activity.path ? () => onItemClick(activity.path) : undefined
            }
            title={activity.definition || undefined}
            aria-label={activity.definition || activity.title}
            direction="row"
            spacing={1.25}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              py: { xs: 1.25, lg: 1.1 },
              px: activity.path ? 0.5 : 0,
              border: 0,
              bgcolor: "transparent",
              font: "inherit",
              textAlign: "left",
              cursor: activity.path ? "pointer" : "default",
              borderRadius: "6px",
              "&:hover": activity.path ? { bgcolor: "#f8fafc" } : undefined,
              "&:focus-visible": activity.path
                ? {
                    outline: "2px solid #2563eb",
                    outlineOffset: 2,
                  }
                : undefined,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                {activity.title}
              </Typography>
            </Box>
            <Chip size="small" color={activity.color} label={activity.type} />
          </Stack>
        ))}
      </Stack>
    </SectionPanel>
  );
}

function RiskQueuePanel({ items, onItemClick }) {
  return (
    <SectionPanel title="우선 처리 항목" sx={lowerPanelSx}>
      <Stack
        spacing={{ xs: 1.35, lg: 1.25 }}
        sx={{
          flex: 1,
          minHeight: 0,
          justifyContent: items.length > 5 ? "flex-start" : "center",
          overflow: "auto",
          pr: 0.25,
        }}
      >
        {items.map((item) => (
          <Box
            key={item.label}
            component={item.path ? "button" : "div"}
            type={item.path ? "button" : undefined}
            onClick={item.path ? () => onItemClick(item.path) : undefined}
            title={item.definition || undefined}
            aria-label={item.definition || item.label}
            sx={{
              width: "100%",
              p: item.path ? 0.5 : 0,
              m: item.path ? -0.5 : 0,
              border: 0,
              bgcolor: "transparent",
              font: "inherit",
              textAlign: "left",
              borderRadius: "6px",
              cursor: item.path ? "pointer" : "default",
              "&:hover": item.path ? { bgcolor: "#f8fafc" } : undefined,
              "&:focus-visible": item.path
                ? {
                    outline: "2px solid #2563eb",
                    outlineOffset: 2,
                  }
                : undefined,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 0.6 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {item.value}
                {item.unit || "건"}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={item.progress}
              sx={{
                height: 9,
                borderRadius: 999,
                bgcolor: "#f1f5f9",
                "& .MuiLinearProgress-bar": {
                  bgcolor: item.color,
                  borderRadius: 999,
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </SectionPanel>
  );
}

function RiskAssessmentMain() {
  const { group_code, project_code } = useParams();
  const { safeAppMode } = useOutletContext() || {};
  const navigate = useNavigate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const isTabletDown = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = isPhone || (safeAppMode === "worker" && isTabletDown);
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [isSafetyCalendarOpen, setIsSafetyCalendarOpen] = useState(false);
  const [selectedSafetyCalendarDay, setSelectedSafetyCalendarDay] =
    useState(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  const groupCode = group_code || "dev";
  const projectCode = project_code || "ese";
  const basePath = useMemo(
    () => `/safe/${groupCode}/${projectCode}`,
    [groupCode, projectCode]
  );

  useEffect(() => {
    const refresh = () => setDashboardRefreshKey((prev) => prev + 1);
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    const current = new Date();
    const year = current.getFullYear();
    const month = current.getMonth() + 1;
    const months = getRecentMonths();
    const todayKey = formatDateParam(current);
    const calendarRangeStart = new Date(
      current.getFullYear(),
      current.getMonth(),
      1
    );
    const calendarRangeEnd = addDays(current, 45);

    const loadDashboard = async () => {
      setDashboard((prev) => ({ ...prev, isLoading: true, isError: false }));

      const [
        approvalResult,
        pendingHazardResult,
        proposalResult,
        improveWaitingResult,
        improveCompletedResult,
        monthlyResults,
        calendarResult,
        inspectionActionOpenResult,
        inspectionActionOverdueResult,
        msdsResult,
      ] = await Promise.all(
        [
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/getRiskAssessmentApproval`
          ),
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/wizard/pending-hazards`,
            {
              params: { eval_date: formatDateParam(current) },
            }
          ),
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/worker-hazard-proposals`,
            {
              params: { status: "pending" },
            }
          ),
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/getStrategyRegister`,
            {
              params: { year, month, machine_id: "all" },
            }
          ),
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/getStrategyResult`,
            {
              params: { year, month, machine_id: "all", filter: "all" },
            }
          ),
          Promise.all(
            months.map((item) =>
              axiosInstance
                .get(`/risk/${groupCode}/${projectCode}/getStrategyResult`, {
                  params: {
                    year: item.year,
                    month: item.month,
                    machine_id: "all",
                    filter: "all",
                  },
                })
                .then((response) => ({
                  ...item,
                  value: (response.data || []).filter(isCompletedImprove)
                    .length,
                }))
                .catch(() => ({ ...item, value: 0 }))
            )
          ),
          axiosInstance.get(`/risk/${groupCode}/${projectCode}/calendar`, {
            params: {
              start: formatDateParam(calendarRangeStart),
              end: formatDateParam(calendarRangeEnd),
            },
          }),
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/inspection/actions`,
            {
              params: { scope: "all", status: "open" },
            }
          ),
          axiosInstance.get(
            `/risk/${groupCode}/${projectCode}/inspection/actions`,
            {
              params: { scope: "all", status: "overdue" },
            }
          ),
          axiosInstance.get(`/risk/${groupCode}/${projectCode}/msds`),
        ].map((promise) => promise.catch((error) => ({ error })))
      );

      if (ignore) return;

      const approvals = approvalResult.error ? [] : approvalResult.data || [];
      const pendingHazards = pendingHazardResult.error
        ? []
        : pendingHazardResult.data?.rows || [];
      const pendingProposals = proposalResult.error
        ? []
        : proposalResult.data || [];
      const improveWaitingRows = improveWaitingResult.error
        ? []
        : improveWaitingResult.data || [];
      const improveCompletedRows = improveCompletedResult.error
        ? []
        : improveCompletedResult.data || [];
      const monthlyCompletion = Array.isArray(monthlyResults)
        ? monthlyResults
        : months.map((item) => ({ ...item, value: 0 }));
      const inspectionActionOpen = inspectionActionOpenResult.error
        ? []
        : inspectionActionOpenResult.data || [];
      const inspectionActionOverdue = inspectionActionOverdueResult.error
        ? []
        : inspectionActionOverdueResult.data || [];
      const msdsItems = msdsResult.error ? [] : msdsResult.data || [];
      const uniquePendingHazards = uniqueRowsBy(
        pendingHazards,
        (row) => row.register_cause_id
      );
      const uniqueInspectionActionOpen = uniqueRowsBy(
        inspectionActionOpen,
        (row) => row.id
      );
      const uniqueInspectionActionOverdue = uniqueRowsBy(
        inspectionActionOverdue,
        (row) => row.id
      );
      let calendarData = calendarResult.error
        ? null
        : calendarResult.data || null;

      if (!calendarData) {
        const [legacyEducationResult, legacyTbmResult] = await Promise.all(
          [
            axiosInstance.get(
              `/risk/${groupCode}/${projectCode}/edu/schedules`,
              {
                params: {
                  start: formatDateParam(calendarRangeStart),
                  end: formatDateParam(calendarRangeEnd),
                },
              }
            ),
            axiosInstance.get(
              `/risk/${groupCode}/${projectCode}/tbm/calendar`,
              {
                params: {
                  start: formatDateParam(calendarRangeStart),
                  end: formatDateParam(calendarRangeEnd),
                },
              }
            ),
          ].map((promise) => promise.catch((error) => ({ error })))
        );

        const legacyEducation = legacyEducationResult.error
          ? []
          : legacyEducationResult.data || [];
        const legacyTbm = legacyTbmResult.error
          ? []
          : legacyTbmResult.data || [];
        const legacyInspection = uniqueRowsBy(
          [...uniqueInspectionActionOpen, ...uniqueInspectionActionOverdue],
          (row) => row.id
        );
        calendarData = {
          days: buildSafetyCalendarDays({
            start: current,
            days: 7,
            tbmItems: legacyTbm,
            educationItems: legacyEducation,
            inspectionItems: legacyInspection,
            msdsItems,
          }),
          events: buildLegacyCalendarEvents({
            tbmItems: legacyTbm,
            educationItems: legacyEducation,
            inspectionItems: legacyInspection,
            msdsItems,
          }),
          lists: {
            education: legacyEducation,
            tbm: legacyTbm,
          },
        };
      }

      if (ignore) return;

      const upcomingEducation = (calendarData?.lists?.education || []).filter(
        (item) => getDateKey(item.edu_day) >= todayKey
      );
      const tbmCalendar = calendarData?.lists?.tbm || [];
      const calendarInspectionActions =
        calendarData?.lists?.inspection_actions ||
        uniqueRowsBy(
          [...uniqueInspectionActionOpen, ...uniqueInspectionActionOverdue],
          (row) => row.id
        );
      const calendarMsdsItems = calendarData?.lists?.msds || msdsItems;
      const todayTbm = getTodayTbmSummary(tbmCalendar, todayKey);
      const calendarLoadFailed = Boolean(
        calendarResult.error && !calendarData?.days?.length
      );
      const msdsExpiring = msdsItems.filter((row) => {
        const diff = daysBetween(todayKey, row.expire_at);
        return diff !== null && diff >= 0 && diff <= 30;
      }).length;
      const msdsExpired = msdsItems.filter((row) => {
        const diff = daysBetween(todayKey, row.expire_at);
        return diff !== null && diff < 0;
      }).length;
      const upcomingTbm = tbmCalendar
        .flatMap((day) => day.sessions || [])
        .filter(
          (item) =>
            getDateKey(item.session_day || item.session_date_input) >= todayKey
        )
        .sort((a, b) =>
          String(a.session_date_input || "").localeCompare(
            String(b.session_date_input || "")
          )
        );
      const safetyCalendar = buildSafetyCalendarDays({
        start: current,
        days: 7,
        tbmItems: tbmCalendar,
        educationItems: upcomingEducation,
        inspectionItems: calendarInspectionActions,
        msdsItems: calendarMsdsItems,
      });
      const calendarEvents = normalizeCalendarEvents(
        calendarData?.events || []
      );

      setDashboard({
        pendingHazards: uniquePendingHazards.length,
        approvalPending: approvals.filter(
          (row) => toNumber(row.eval_status) < 20
        ).length,
        approvalCompleted: approvals.filter(
          (row) => toNumber(row.eval_status) >= 20
        ).length,
        improveWaiting: improveWaitingRows.filter(
          (row) => !isCompletedImprove(row)
        ).length,
        improveOverdue: improveWaitingRows.filter(isOverdueImprove).length,
        improveCompleted:
          improveCompletedRows.filter(isCompletedImprove).length,
        pendingProposals: pendingProposals.length,
        inspectionActionOpen: uniqueInspectionActionOpen.length,
        inspectionActionOverdue: uniqueInspectionActionOverdue.length,
        todayTbmCompleted: todayTbm.completed,
        todayTbmMissed: todayTbm.missed,
        todayTbmUnregistered: todayTbm.unregistered,
        msdsExpiring,
        msdsExpired,
        monthlyCompletion,
        upcomingEducation,
        upcomingTbm,
        msdsItems,
        tbmCalendar,
        safetyCalendar,
        calendarEvents,
        isLoading: false,
        isError: Boolean(
          approvalResult.error ||
            pendingHazardResult.error ||
            proposalResult.error ||
            improveWaitingResult.error ||
            improveCompletedResult.error ||
            calendarLoadFailed ||
            inspectionActionOpenResult.error ||
            inspectionActionOverdueResult.error ||
            msdsResult.error
        ),
      });
    };

    loadDashboard();

    return () => {
      ignore = true;
    };
  }, [groupCode, projectCode, dashboardRefreshKey]);

  const kpiItems = [
    {
      label: "미평가 항목",
      value: dashboard.pendingHazards,
      color: "#2563eb",
      bg: "#eff6ff",
      icon: <AssessmentIcon />,
      path: "/assessment/wizard",
      definition: "이번 달 아직 평가 저장이 없는 활성 평가대상입니다.",
    },
    {
      label: "평가 완료",
      value: dashboard.approvalCompleted,
      color: "#0ea5e9",
      bg: "#f0f9ff",
      icon: <AssignmentTurnedInIcon />,
      path: "/monitoring/integrated-report",
      definition: "평가 저장 후 승인 완료 상태로 처리된 항목입니다.",
    },
    {
      label: "개선 대기",
      value: dashboard.improveWaiting,
      color: "#dc2626",
      bg: "#fef2f2",
      icon: <ReportProblemIcon />,
      path: "/assessment/wizard",
      definition: "개선대책이 필요하고 아직 완료 처리되지 않은 항목입니다.",
    },
    {
      label: "개선 완료",
      value: dashboard.improveCompleted,
      color: "#16a34a",
      bg: "#f0fdf4",
      icon: <FactCheckIcon />,
      path: "/monitoring/integrated-report",
      definition: "개선 후 사진과 완료일 기준으로 완료 처리된 항목입니다.",
    },
  ];

  const queueItems = [
    dashboard.todayTbmMissed > 0 && {
      label: "오늘 TBM 미실시",
      value: dashboard.todayTbmMissed,
      unit: "팀",
      progress: Math.min(100, dashboard.todayTbmMissed * 22),
      color: "#dc2626",
      path: "/monitoring/tbm",
      definition: "오늘 기준 TBM 실시 기록이 없는 팀입니다.",
    },
    dashboard.todayTbmUnregistered > 0 && {
      label: "오늘 TBM 미등록",
      value: dashboard.todayTbmUnregistered,
      unit: "팀",
      progress: Math.min(100, dashboard.todayTbmUnregistered * 18),
      color: "#f59e0b",
      path: "/monitoring/tbm",
      definition: "오늘 기준 아직 TBM 기록이 등록되지 않은 팀입니다.",
    },
    {
      label: "개선 기한 초과",
      value: dashboard.improveOverdue,
      progress: Math.min(100, dashboard.improveOverdue * 18),
      color: "#dc2626",
      path: "/assessment/wizard",
      definition: "개선 예정일이 지났지만 완료 처리되지 않은 개선대책입니다.",
    },
    {
      label: "점검 조치요청",
      value: dashboard.inspectionActionOpen,
      progress: Math.min(100, dashboard.inspectionActionOpen * 15),
      color: "#ea580c",
      path: "/monitoring/actions",
      definition: "안전점검에서 조치가 필요하고 아직 완료되지 않은 항목입니다.",
    },
    {
      label: "점검 기한초과",
      value: dashboard.inspectionActionOverdue,
      progress: Math.min(100, dashboard.inspectionActionOverdue * 18),
      color: "#dc2626",
      path: "/monitoring/actions",
      definition: "안전점검 조치 기한이 지난 미완료 항목입니다.",
    },
    {
      label: "근로자 제안 검토",
      value: dashboard.pendingProposals,
      progress: Math.min(100, dashboard.pendingProposals * 15),
      color: "#f59e0b",
      path: "/worker/hazard-review",
      definition:
        "근로자 모바일 제안 중 아직 승인 또는 거절하지 않은 항목입니다.",
    },
    {
      label: "MSDS 만료 예정",
      value: dashboard.msdsExpiring,
      progress: Math.min(100, dashboard.msdsExpiring * 18),
      color: "#7c3aed",
      path: "/standard/msds",
      definition: "만료일이 30일 이내로 다가온 활성 MSDS입니다.",
    },
  ].filter(Boolean);

  const activities = [
    dashboard.todayTbmMissed > 0 && {
      title: `오늘 TBM 미실시 ${dashboard.todayTbmMissed}팀`,
      type: "TBM",
      color: "error",
      path: "/monitoring/tbm",
      definition: "오늘 TBM을 실시하지 않은 팀입니다.",
    },
    dashboard.todayTbmUnregistered > 0 && {
      title: `오늘 TBM 미등록 ${dashboard.todayTbmUnregistered}팀`,
      type: "TBM",
      color: "warning",
      path: "/monitoring/tbm",
      definition: "오늘 TBM 기록이 아직 등록되지 않은 팀입니다.",
    },
    dashboard.pendingProposals > 0 && {
      title: `근로자 제안 검토 ${dashboard.pendingProposals}건`,
      type: "제안",
      color: "info",
      path: "/worker/hazard-review",
      definition: "검토 대기 상태의 근로자 유해·위험요인 제안입니다.",
    },
    dashboard.improveOverdue > 0 && {
      title: `개선 기한 초과 ${dashboard.improveOverdue}건`,
      type: "개선",
      color: "error",
      path: "/assessment/wizard",
      definition: "개선 예정일이 지났지만 완료 처리되지 않은 개선대책입니다.",
    },
    dashboard.inspectionActionOpen > 0 && {
      title: `안전점검 조치요청 ${dashboard.inspectionActionOpen}건`,
      type: "점검",
      color: "warning",
      path: "/monitoring/actions",
      definition: "안전점검 이후 담당자 조치가 필요한 항목입니다.",
    },
    dashboard.inspectionActionOverdue > 0 && {
      title: `안전점검 기한초과 ${dashboard.inspectionActionOverdue}건`,
      type: "지연",
      color: "error",
      path: "/monitoring/actions",
      definition: "안전점검 조치 기한을 넘긴 항목입니다.",
    },
    dashboard.msdsExpired > 0 && {
      title: `MSDS 만료 ${dashboard.msdsExpired}건`,
      type: "MSDS",
      color: "error",
      path: "/standard/msds",
      definition: "만료일이 지난 활성 MSDS입니다.",
    },
    dashboard.msdsExpiring > 0 && {
      title: `MSDS 만료 예정 ${dashboard.msdsExpiring}건`,
      type: "MSDS",
      color: "warning",
      path: "/standard/msds",
      definition: "만료일이 30일 이내로 다가온 활성 MSDS입니다.",
    },
    dashboard.pendingHazards > 0 && {
      title: `미평가 항목 ${dashboard.pendingHazards}건`,
      type: "평가",
      color: "primary",
      path: "/assessment/wizard",
      definition: "이번 달 아직 평가 저장이 없는 활성 평가대상입니다.",
    },
  ].filter(Boolean);

  const safeActivities = activities.length
    ? activities
    : [
        {
          title: "현재 즉시 처리할 알림이 없습니다",
          type: "정상",
          color: "success",
        },
      ];
  const quickActions = isMobile
    ? QUICK_ACTIONS.filter(
        (item) =>
          !["/assessment/wizard", "/monitoring/integrated-report"].includes(
            item.path
          )
      )
    : QUICK_ACTIONS;

  const goTo = (path) => {
    navigate(`${basePath}${path}`);
  };

  const openSafetyCalendarDay = (day) => {
    if (!day?.date) return;
    if (!Array.isArray(day.items) || day.items.length === 0) {
      toast.info("해당 날짜에는 표시할 안전상황 알림이 없습니다.");
      return;
    }
    setSelectedSafetyCalendarDay(day);
  };

  if (isMobile) {
    return (
      <MobileHomeDashboard
        kpiItems={kpiItems}
        actions={MOBILE_HOME_ACTIONS}
        educationItems={dashboard.upcomingEducation}
        todayTbm={{
          completed: dashboard.todayTbmCompleted,
          missed: dashboard.todayTbmMissed + dashboard.todayTbmUnregistered,
        }}
        isLoading={dashboard.isLoading}
        onNavigate={goTo}
      />
    );
  }

  return (
    <Box
      sx={{
        height: { xs: "auto", lg: "calc(100vh - 60px)" },
        p: { xs: 1.5, md: 3 },
        bgcolor: "#f3f6fa",
        overflow: { xs: "auto", lg: "hidden" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack spacing={{ xs: 2.5, lg: 1.5 }} sx={{ flex: 1, minHeight: 0 }}>
        <Paper
          elevation={0}
          sx={{
            ...panelSx,
            p: { xs: 2.25, md: 2 },
            width: "100%",
            flexShrink: 0,
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: 900, letterSpacing: 0 }}
          >
            위험성평가 관리 현황
          </Typography>
        </Paper>

        <KpiBoard
          items={kpiItems.map((item) =>
            dashboard.isLoading ? { ...item, value: "-" } : item
          )}
          onItemClick={goTo}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(12, minmax(0, 1fr))",
            },
            gridTemplateRows: {
              xs: "auto",
              lg: "minmax(250px, 1.08fr) minmax(210px, 0.92fr)",
            },
            gridAutoRows: { xs: "auto" },
            "& > *": {
              minHeight: 0,
              display: "flex",
            },
          }}
        >
          <Box sx={{ gridColumn: { xs: "1", lg: "1 / span 9" } }}>
            <SectionPanel title="주요 관리 기능" sx={tallPanelSx}>
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(5, minmax(0, 1fr))",
                  },
                  gridTemplateRows: { lg: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.5,
                }}
              >
                {quickActions.map((item) => (
                  <QuickActionCard
                    key={item.title}
                    item={item}
                    onClick={() => goTo(item.path)}
                  />
                ))}
              </Box>
            </SectionPanel>
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "10 / span 3" } }}>
            <RecentActivityPanel
              activities={safeActivities}
              onItemClick={goTo}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "1 / span 4" } }}>
            <MonthlyChart
              data={
                dashboard.monthlyCompletion.length
                  ? dashboard.monthlyCompletion
                  : getRecentMonths().map((item) => ({ ...item, value: 0 }))
              }
            />
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "5 / span 4" } }}>
            <UpcomingSchedulePanel
              educationItems={dashboard.upcomingEducation}
              calendarDays={dashboard.safetyCalendar}
              isLoading={dashboard.isLoading}
              onOpenCalendar={() => setIsSafetyCalendarOpen(true)}
              onOpenCalendarDay={openSafetyCalendarDay}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: "1", lg: "9 / span 4" } }}>
            <RiskQueuePanel items={queueItems} onItemClick={goTo} />
          </Box>
        </Box>
      </Stack>
      <SafetyCalendarDialog
        open={isSafetyCalendarOpen}
        onClose={() => setIsSafetyCalendarOpen(false)}
        events={dashboard.calendarEvents}
        isMobile={isMobile}
        onDateClick={openSafetyCalendarDay}
      />
      <SafetyCalendarDayDialog
        open={Boolean(selectedSafetyCalendarDay)}
        onClose={() => setSelectedSafetyCalendarDay(null)}
        day={selectedSafetyCalendarDay}
        events={dashboard.calendarEvents}
      />
    </Box>
  );
}

export { RiskAssessmentMain };
